/**
 * TrustBadge Component
 *
 * Displays a regulatory, security, or partner trust badge with icon, title,
 * and optional subtitle. Used across the AA flow to reinforce user trust.
 *
 * @example
 * ```tsx
 * <TrustBadge
 *   icon={<Landmark className="h-4 w-4" />}
 *   title="RBI Regulated"
 *   subtitle="Account Aggregator Framework"
 *   link="https://sahamati.org.in/what-is-account-aggregator/"
 * />
 * ```
 */

import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface TrustBadgeProps {
  icon: ReactNode;
  title: string;
  subtitle?: string;
  link?: string;
  className?: string;
}

export function TrustBadge({
  icon,
  title,
  subtitle,
  link,
  className,
}: TrustBadgeProps) {
  const content = (
    <div
      className={cn(
        "flex items-center gap-2.5 rounded-lg border border-border bg-muted/30 px-3 py-2.5",
        link && "hover:bg-muted/50 transition-colors",
        className
      )}
    >
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-sm font-medium text-foreground">{title}</div>
        {subtitle && (
          <div className="text-xs text-muted-foreground">{subtitle}</div>
        )}
      </div>
    </div>
  );

  if (link) {
    return (
      <a
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg"
        aria-label={`${title}${subtitle ? ` — ${subtitle}` : ""}`}
      >
        {content}
      </a>
    );
  }

  return content;
}
